import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Shield, Check, ShoppingCart, FileText, ChevronLeft, Loader2, Phone } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import AccessibilityWidget from './AccessibilityWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(price);

const THERMOSTAT_IMG = "https://customer-assets.emergentagent.com/job_cooling-converter/artifacts/kvo2sll9_ProStat_PRODUCT-Details_720x550_PRS4110-PRS4210%281%29.jpg";

export default function ProductPage({ onAddToCart, onRequestQuote, onOpenLeadForm, onOpenEstimateForm, cartItems, onOpenCart }) {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSalePrice, setShowSalePrice] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${API}/products/${id}`);
        setProduct(res.data);
      } catch {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    onAddToCart(product);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header onOpenLeadForm={onOpenLeadForm} onOpenEstimateForm={onOpenEstimateForm} cartItems={cartItems} onOpenCart={onOpenCart} />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="ml-3 text-gray-600">Loading product...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header onOpenLeadForm={onOpenLeadForm} onOpenEstimateForm={onOpenEstimateForm} cartItems={cartItems} onOpenCart={onOpenCart} />
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <Link to="/#products" className="text-emerald-600 hover:text-emerald-700 font-medium">
            Back to all products
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="product-page">
      <Header onOpenLeadForm={onOpenLeadForm} cartItems={cartItems} onOpenCart={onOpenCart} />

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Breadcrumb */}
        <Link
          to="/unit-only"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-emerald-600 transition-colors mb-8"
          data-testid="back-to-products"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Unit Only products
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Image */}
          <div>
            <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-8 aspect-square border border-gray-100">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-contain"
                  data-testid="product-page-image"
                />
              ) : (
                <div className="text-center">
                  <div className="font-bold text-5xl text-emerald-700">{product.tonnage}</div>
                  <div className="text-lg text-gray-500 mt-2">{product.seer_rating}</div>
                </div>
              )}
            </div>
          </div>

          {/* Right - Details */}
          <div>
            <span className="inline-block bg-blue-500 text-white text-xs font-semibold uppercase tracking-wide px-3 py-1.5 rounded-md mb-4">
              Unit Only
            </span>

            <h1 className="text-3xl font-bold text-gray-900 leading-snug mb-3" data-testid="product-page-title">
              {product.name}
            </h1>

            <p className="text-gray-400 text-sm mb-6" data-testid="product-page-model">
              Model: {product.model_number}
            </p>

            {/* Specs */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                <p className="text-xs text-gray-400 mb-0.5">Tonnage</p>
                <p className="font-bold text-gray-900">{product.tonnage}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                <p className="text-xs text-gray-400 mb-0.5">Efficiency</p>
                <p className="font-bold text-gray-900">{product.seer_rating}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                <p className="text-xs text-gray-400 mb-0.5">Refrigerant</p>
                <p className="font-bold text-gray-900">{product.refrigerant}</p>
              </div>
            </div>

            {/* Features */}
              <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">What's Included</h3>
              <div className="grid grid-cols-2 gap-2">
                {['Air Handler', 'Condenser', 'Heating Element'].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Section */}
            <div className="border-t border-gray-100 pt-6">
              {/* MAP Price */}
              <div className="mb-4" data-testid="product-page-map-price">
                <span className="font-bold text-3xl text-gray-900">{formatPrice(product.map_price)}</span>
              </div>

              <button
                onClick={handleAddToCart}
                className="w-full px-6 py-4 rounded-lg transition-colors text-lg"
                style={{ backgroundColor: '#FACC15', color: '#DC2626', fontWeight: 900 }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FDE047'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#FACC15'}
                data-testid="product-page-reveal-btn"
              >
                {addedToCart ? 'Added to Cart!' : 'Add to Cart For Discount'}
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <AccessibilityWidget />
    </div>
  );
}
